/**
 * Cross-platform storage adapter
 *
 * Abstracts storage implementation to work in both web and native environments:
 * - Web: uses localStorage (remember me) or sessionStorage (session only)
 * - Native (Capacitor): uses Preferences API
 *
 * This allows MobileEmulatorPage.tsx to use a single API regardless of platform.
 *
 * REMEMBER ME FEATURE:
 * - If "rememberMe" is true in localStorage, uses localStorage (persists after browser close)
 * - If "rememberMe" is false/absent, uses sessionStorage (cleared on browser close)
 */

import { Preferences } from '@capacitor/preferences';
import { Capacitor } from '@capacitor/core';

/**
 * Detects if app is running in native Capacitor context
 */
const isNative = (): boolean => {
  return Capacitor.isNativePlatform();
};

/**
 * Checks if "Remember Me" is enabled
 * Note: This preference is always stored in localStorage (not sessionStorage)
 */
const isRememberMeEnabled = (): boolean => {
  return localStorage.getItem('rememberMe') === 'true';
};

/**
 * Gets the appropriate web storage based on "Remember Me" setting
 */
const getWebStorage = (): Storage => {
  return isRememberMeEnabled() ? localStorage : sessionStorage;
};

/**
 * Cross-platform storage interface
 */
export const storage = {
  /**
   * Get item from storage
   * @param key Storage key
   * @returns Promise resolving to value or null if not found
   */
  async getItem(key: string): Promise<string | null> {
    if (isNative()) {
      // Native: use Capacitor Preferences
      const { value } = await Preferences.get({ key });
      return value;
    } else {
      // Web: try preferred storage first, then fallback to other
      const preferred = getWebStorage();
      let value = preferred.getItem(key);

      // Fallback: if not found, check the other storage
      // (handles case where user toggled "remember me" between sessions)
      if (!value) {
        const fallback = isRememberMeEnabled() ? sessionStorage : localStorage;
        value = fallback.getItem(key);
      }

      return value;
    }
  },

  /**
   * Set item in storage
   * @param key Storage key
   * @param value Value to store (will be stringified)
   */
  async setItem(key: string, value: string): Promise<void> {
    if (isNative()) {
      // Native: use Capacitor Preferences
      await Preferences.set({ key, value });
    } else {
      // Web: use storage based on "Remember Me" setting
      const webStorage = getWebStorage();
      webStorage.setItem(key, value);
    }
  },

  /**
   * Remove item from storage
   * @param key Storage key
   */
  async removeItem(key: string): Promise<void> {
    if (isNative()) {
      // Native: use Capacitor Preferences
      await Preferences.remove({ key });
    } else {
      // Web: remove from BOTH storages to ensure clean state
      localStorage.removeItem(key);
      sessionStorage.removeItem(key);
    }
  },

  /**
   * Clear all storage
   */
  async clear(): Promise<void> {
    if (isNative()) {
      // Native: use Capacitor Preferences
      await Preferences.clear();
    } else {
      // Web: clear BOTH storages
      localStorage.clear();
      sessionStorage.clear();
    }
  },

  /**
   * Get all keys from storage
   * @returns Promise resolving to array of keys
   */
  async keys(): Promise<string[]> {
    if (isNative()) {
      // Native: use Capacitor Preferences
      const { keys } = await Preferences.keys();
      return keys;
    } else {
      // Web: combine keys from both storages (unique)
      const localKeys = Object.keys(localStorage);
      const sessionKeys = Object.keys(sessionStorage);
      return [...new Set([...localKeys, ...sessionKeys])];
    }
  }
};
